<template>
  <v-dialog v-model="dialog" scrollable width="854">
    <v-card>
      <v-toolbar dense color="primary" dark>
        <v-toolbar-title>
          {{
            pitch._id
              ? $t('titles.global.updateModel', { field: $tc('models.pitch') })
              : $t('titles.global.newModel', { field: $tc('models.pitch') })
          }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn dark icon @click="cancel">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-layout row wrap>
          <v-flex md6 xs12 px-2>
            <v-text-field
              v-model="pitch.name"
              v-validate="'required'"
              autofocus
              :readonly="!edit"
              :label="$t('fields.global.name') + ' (*)'"
              :data-vv-name="$t('fields.global.name')"
              :error-messages="errors.collect($t('fields.global.name'))"
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-autocomplete
              v-model="pitch.stadiumId"
              v-validate="'required'"
              readonly
              :items="stadiumSelectList"
              :label="
                $t('action.actions.select', { field: $tc('models.stadium') }) +
                ' (*)'
              "
              :data-vv-name="$tc('models.stadium')"
              :error-messages="errors.collect($tc('models.stadium'))"
            >
              <template #selection="data">
                <v-chip
                  color="white"
                  :selected="data.selected"
                  class="chip--select-multi"
                >
                  <v-avatar>
                    <img :src="data.item.photo" />
                  </v-avatar>
                  {{ data.item.text }}
                </v-chip>
              </template>
              <template #item="data">
                <v-list-tile-avatar>
                  <img :src="data.item.photo" />
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title v-html="data.item.text" />
                </v-list-tile-content>
              </template>
            </v-autocomplete>
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-autocomplete
              v-model="pitch.cameraId"
              :items="cameraSelectList"
              :label="
                $t('action.actions.select', { field: $tc('models.camera') })
              "
            />
          </v-flex>
          <v-layout row wrap>
            <v-flex md4 xs12>
              <v-text-field
                v-model="titleFile"
                :disabled="true"
                prepend-icon="attach_file"
              >
                <template slot="label">
                  {{ $t('fields.users.image') }}
                </template>
              </v-text-field>
              <upload-button
                :file-changed-callback="fileChanged"
                :title="titleButton"
                small
                :disabled="!edit"
              />
            </v-flex>
            <v-flex v-if="image" md4 xs12>
              <div style="max-width: 150px">
                <clipper-preview class="basic md" name="preview" />
              </div>
            </v-flex>
            <v-flex v-if="image" md4 xs12>
              <div style="max-width: 150px">
                <clipper-basic
                  :src="image"
                  class="basic lg"
                  preview="preview"
                />
              </div>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <template v-if="edit">
          <span style="color: red">(*) &nbsp;</span>
          <span>{{ $t('action.messages.fieldRequired') }}</span>
        </template>
        <v-spacer />
        <template v-if="edit">
          <meta-button type="reset" :disabled="loading" @click="clear" />
          <meta-button
            v-if="!pitch._id"
            type="create"
            :loading="loading"
            @click="submit()"
          />
          <meta-button
            v-else
            type="update"
            :loading="loading"
            @click="submit()"
          />
        </template>
        <meta-button type="cancel" @click="cancel" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { clipperBasic, clipperPreview } from 'vuejs-clipper'
import UploadButton from 'vuetify-upload-button'
import { mapActions, mapGetters, mapState } from 'vuex'
import { parseObject } from '@/helpers/utils'

export default {
  name: 'PitchForm',
  components: {
    UploadButton,
    clipperBasic,
    clipperPreview,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
      required: true,
    },
    edit: {
      type: Boolean,
      default: true,
      required: false,
    },
    stadiumId: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      pitch: {},

      image: '',
      file: '',
      titleFile: '',
    }
  },
  watch: {
    dialog(val) {
      if (!val) this.cancel()
    },
    stadiumId(val) {
      if (val) {
        this.pitch.stadiumId = val
      }
    },
    selected: {
      handler: function (val) {
        this.setObject(val)
      },
      immediate: true,
      deep: true,
    },
    'pitch.photo': {
      handler: function (val) {
        if (val) {
          if (val.includes('http')) this.image = val
          else this.image = this.defaultValues.imageUrl + val
        } else {
          this.image = ''
        }
        this.titleFile = val
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.setObject(this.selected)
  },
  computed: {
    ...mapState({
      selected: (state) => state.pitches.selected,
      loading: (state) => state.pitches.loading,
    }),
    ...mapGetters({
      stadiumSelectList: 'stadiums/selectList',
      cameraSelectList: 'cameras/selectList',
    }),
    titleButton() {
      return this.image
        ? this.$t('action.actions.change', {
            field: this.$t('fields.users.image'),
          })
        : this.$t('action.actions.upload', {
            field: this.$t('fields.users.image'),
          })
    },
  },
  methods: {
    ...mapActions({
      setCreate: 'pitches/create',
      setUpdate: 'pitches/update',
      reset: 'pitches/reset',
      uploadFile: 'files/uploadPush',
      fetchStadiums: 'stadiums/fetchSelectList',
    }),
    setObject() {
      this.pitch = parseObject(this.selected)
      if (this.stadiumId) this.pitch.stadiumId = this.stadiumId
    },
    submit() {
      this.validate().then((valid) => {
        if (!valid) return
        let payload = Object.assign({}, this.pitch)
        if (!payload._id) {
          if (this.stadiumId) payload.stadiumId = this.stadiumId
          this.setCreate(payload).then((item) => {
            if (this.file) {
              this.uploadFile({
                id: item._id,
                model: 'Pitches',
                field: 'photo',
                file: this.file,
              })
            }
            this.cancel()
          })
        } else {
          this.setUpdate(payload).then((item) => {
            if (this.file) {
              this.uploadFile({
                id: item._id,
                model: 'Pitches',
                field: 'photo',
                file: this.file,
              })
            }
            this.cancel()
          })
        }
      })
    },
    fileChanged(file) {
      if (file.size < 1) return
      this.file = file
      this.titleFile = file.name
      const reader = new FileReader()
      const vm = this

      reader.onload = (e) => {
        vm.image = e.target.result
      }
      reader.readAsDataURL(file)
    },
    cancel() {
      this.$emit('close')
      this.clear()
    },
    clear() {
      this.$nextTick((_) => {
        this.reset()
        this.$validator.reset()
      })
    },
  },
}
</script>
